import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getAvatarSrc } from "@/components/user-avatar";
import { getDisplayName } from "@/lib/task-query-utils";
import { Calendar, ChevronRight, ClipboardList, StickyNote, MessageSquare, Paperclip } from "lucide-react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/lib/auth-context";

interface MeetingTask {
  id: string;
  boardId: string;
  boardName: string;
  title: string;
  statusName: string;
  dueDate: string | null;
  priority: string | null;
  assigneeUserId: string | null;
  assigneeUsername: string | null;
  assigneeFirstName: string | null;
  assigneeLastName: string | null;
  assigneeAvatarUrl: string | null;
  animalCount: number;
  firstAnimalName: string | null;
  interactionCount: number;
  checklist: { done: number; total: number } | null;
  hasNotes: boolean;
  attachmentCount?: number;
}

interface MyMeetingsResponse {
  tasks: MeetingTask[];
  totalCount: number;
  page: number;
  pageSize: number;
  availableStatuses: string[];
  availableBoards: Array<{ id: string; name: string; userRole: string }>;
}

function getInitials(name: string | null, username: string | null): string {
  if (name) return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  if (username) return username.slice(0, 2).toUpperCase();
  return "?";
}

function formatMeetingDate(dueDate: string): string {
  const d = new Date(dueDate + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

function isOverdue(dueDate: string | null): boolean {
  if (!dueDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return new Date(dueDate + "T00:00:00") < today;
}

function MeetingAgendaPill({ meeting }: { meeting: MeetingTask }) {
  const [open, setOpen] = useState(false);

  const agendaTotal = meeting.checklist?.total ?? 0;
  const agendaDone = meeting.checklist?.done ?? 0;

  const attachments = meeting.attachmentCount ?? 0;
  const hasContent = agendaTotal > 0 || meeting.animalCount > 0 || meeting.interactionCount > 0 || attachments > 0;
  if (!hasContent) return null;

  const { data: animals } = useQuery<Array<{
    id: string;
    animalId: string;
    animalName: string | null;
    animalSpecies: string | null;
    animalPhotoUrl: string | null;
  }>>({
    queryKey: ["/api/tasks", meeting.id, "animals"],
    enabled: open && meeting.animalCount > 0,
    staleTime: 30_000,
  });

  const parts: string[] = [];
  if (agendaTotal > 0) parts.push(`${agendaDone}/${agendaTotal}`);
  if (meeting.animalCount > 0) parts.push(`${meeting.animalCount} animal${meeting.animalCount !== 1 ? "s" : ""}`);
  if (meeting.interactionCount > 0) parts.push(`${meeting.interactionCount} interaction${meeting.interactionCount !== 1 ? "s" : ""}`);
  if (attachments > 0) parts.push(`${attachments} file${attachments !== 1 ? "s" : ""}`);
  const pillLabel = parts.join(" \u00B7 ");

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <div
          className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full cursor-default"
          data-testid={`pill-meeting-agenda-${meeting.id}`}
        >
          <ClipboardList className="h-3 w-3" />
          <span className="font-medium">{pillLabel}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="end" className="p-3 max-w-[280px]">
        <div className="flex flex-col gap-2" data-testid={`tooltip-meeting-agenda-${meeting.id}`}>
          {agendaTotal > 0 && (
            <div className="text-xs font-medium">
              Agenda: {agendaDone} of {agendaTotal} items complete
            </div>
          )}
          {meeting.animalCount > 0 && (
            <div className="flex flex-col gap-1.5">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                Linked Animals ({meeting.animalCount})
              </div>
              {animals ? (
                animals.map((animal) => (
                  <div key={animal.id} className="flex items-center gap-2">
                    <span className="text-xs truncate flex-1">{animal.animalName || animal.animalId}</span>
                  </div>
                ))
              ) : (
                <span className="text-xs text-muted-foreground">Loading...</span>
              )}
            </div>
          )}
          {meeting.interactionCount > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1 border-t">
              <MessageSquare className="h-3 w-3" />
              <span>{meeting.interactionCount} interaction{meeting.interactionCount !== 1 ? "s" : ""}</span>
            </div>
          )}
          {attachments > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1 border-t">
              <Paperclip className="h-3 w-3" />
              <span>{attachments} attachment{attachments !== 1 ? "s" : ""}</span>
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function isUpcomingOrToday(dueDate: string | null): boolean {
  if (!dueDate) return true;
  const d = new Date(dueDate + "T23:59:59");
  return d >= new Date();
}

export function DashboardMeetingsWidget() {
  const [showAll, setShowAll] = useState(false);
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("status", "all");
    params.set("page", "1");
    params.set("pageSize", "15");
    return params.toString();
  }, []);

  const { data, isLoading } = useQuery<MyMeetingsResponse>({
    queryKey: ["/api/tasks/my-meetings?" + queryParams],
  });

  const allMeetings = data?.tasks ?? [];

  const filteredMeetings = useMemo(() => {
    let result = allMeetings.filter(m => isUpcomingOrToday(m.dueDate));
    if (!showAll && user) {
      result = result.filter(m => m.assigneeUserId === user.id);
    }
    return result;
  }, [allMeetings, showAll, user]);

  const sortedMeetings = useMemo(() => {
    return [...filteredMeetings].sort((a, b) => {
      if (!a.dueDate && !b.dueDate) return 0;
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return a.dueDate.localeCompare(b.dueDate);
    }).slice(0, 6);
  }, [filteredMeetings]);

  return (
    <Card className="min-w-0 overflow-hidden h-full flex flex-col" data-testid="card-dashboard-meetings">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-3 border-b">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-primary flex-shrink-0" />
          <CardTitle className="text-base whitespace-nowrap">Meeting Agendas</CardTitle>
        </div>
        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          <span className="text-xs text-muted-foreground hidden sm:inline">Meetings:</span>
          <div className="flex items-center bg-muted rounded-md p-0.5" data-testid="toggle-dashboard-meeting-filter">
            <button
              type="button"
              onClick={() => setShowAll(false)}
              className={`px-2 py-0.5 text-xs rounded transition-colors ${!showAll ? "bg-background font-medium text-foreground shadow-sm" : "text-muted-foreground"}`}
              data-testid="button-meetings-mine"
            >
              Mine
            </button>
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className={`px-2 py-0.5 text-xs rounded transition-colors ${showAll ? "bg-background font-medium text-foreground shadow-sm" : "text-muted-foreground"}`}
              data-testid="button-meetings-all"
            >
              All
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0 flex-1 flex flex-col">
        {isLoading ? (
          <div className="p-4 space-y-3" data-testid="loading-dashboard-meetings">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : sortedMeetings.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-6 text-center" data-testid="empty-dashboard-meetings">
            <div>
              <Calendar className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{showAll ? "No meetings scheduled" : "No meetings assigned to you"}</p>
              <Link href="/meetings" className="text-xs text-primary mt-2 inline-block" data-testid="link-view-meetings">View meeting agendas</Link>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <div className="divide-y list-zebra">
            {sortedMeetings.map((meeting) => {
              const facilitatorName = getDisplayName(meeting.assigneeFirstName, meeting.assigneeLastName, meeting.assigneeUsername);
              const overdue = isOverdue(meeting.dueDate);
              return (
                <div
                  key={meeting.id}
                  className="flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => setLocation(`/meetings/${meeting.id}`)}
                  data-testid={`row-dashboard-meeting-${meeting.id}`}
                >
                  <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Avatar className="h-8 w-8 border cursor-default flex-shrink-0">
                          {meeting.assigneeAvatarUrl && meeting.assigneeUserId && (
                            <AvatarImage src={getAvatarSrc(meeting.assigneeAvatarUrl, meeting.assigneeUserId)} alt={facilitatorName} />
                          )}
                          <AvatarFallback className="text-[10px] bg-emerald-50 text-emerald-700 font-bold dark:bg-emerald-900/30 dark:text-emerald-400">
                            {getInitials(facilitatorName, meeting.assigneeUsername)}
                          </AvatarFallback>
                        </Avatar>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="font-medium">Facilitator: {facilitatorName}</p>
                      </TooltipContent>
                    </Tooltip>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold truncate" data-testid={`text-dashboard-meeting-title-${meeting.id}`}>{meeting.title}</span>
                        {meeting.hasNotes && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <StickyNote className="h-3 w-3 text-amber-500 flex-shrink-0" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>Has notes</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground/60">
                        <span className="truncate">{meeting.boardName}</span>
                        {meeting.dueDate && (
                          <>
                            <span className="w-1 h-1 bg-muted-foreground/40 rounded-full flex-shrink-0" />
                            <span className={`whitespace-nowrap flex-shrink-0 ${overdue ? "text-destructive font-medium" : ""}`}>
                              {formatMeetingDate(meeting.dueDate)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-1 sm:ml-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <MeetingAgendaPill meeting={meeting} />
                  </div>
                </div>
              );
            })}
          </div>
          </div>
        )}
      </CardContent>
      {sortedMeetings.length > 0 && (
        <div className="p-3 border-t bg-muted/30">
          <Button variant="ghost" className="w-full text-xs text-muted-foreground" asChild>
            <Link href="/meetings" data-testid="link-all-meetings">
              View All Meetings <ChevronRight className="ml-1 h-3 w-3" />
            </Link>
          </Button>
        </div>
      )}
    </Card>
  );
}
